<script lang="ts">
	import { type Snippet } from 'svelte'
	import { fade, fly } from 'svelte/transition'
	import { goto } from '$app/navigation'
	import { page } from '$app/state'
	import SectionHeading from '$lib/public/SectionHeading.svelte'
	import { appState } from '$lib/app-state.svelte'

	interface Props {
		id: string
		title: string
		autocomplete?: boolean
		large?: boolean
		children?: Snippet
		save?: (form: HTMLFormElement) => Promise<any>
	}

	// Get props and set up reactive state (using your runes style)
	let { id, title, autocomplete = true, large = false, children, save }: Props = $props()
	const titleId = $state(`${id}Title`)
	let isVisible = $state(false)
	let form: HTMLFormElement | undefined = $state()
	let message = $state('')

	$effect(() => {
		if (page.url.hash === `#${id}`) {
			open()
		} else {
			isVisible = false
		}
	})

	const open = async () => {
		isVisible = true
		document.body.style.overflow = 'hidden' // Prevent scrolling
		appState.formInvalid = false // Just in case we missed setting this on close
		// Delay focusing to allow the transition to complete.
		setTimeout(() => {
			const firstField = form?.querySelector<HTMLInputElement | HTMLSelectElement>('input, select')
			firstField?.focus()
		}, 300)
	}

	// Navigate back (clearing the URL hash) using history if possible,
	// or using goto() if needed.
	const back = () => {
		if (document.referrer.startsWith(page.url.origin)) {
			history.back()
		} else {
			const url = page.url.href.replace(page.url.hash, '')
			goto(url, { replaceState: false })
		}
	}

	export const close = () => {
		isVisible = false
		appState.formInvalid = false
		message = ''
		document.body.style.overflow = ''
		back()
	}

	// Close when escape key is pressed
	const onkeydown = (event: KeyboardEvent) => {
		if (isVisible && page.url.hash === `#${id}` && event.key === 'Escape') {
			event.stopPropagation()
			close()
		}
	}

	const onbackdropclick = (event: MouseEvent) => {
		event.stopPropagation()
		close()
	}

	const onsubmit = async (event: SubmitEvent) => {
		event.preventDefault()
		if (!form?.checkValidity()) {
			appState.formInvalid = true
			return
		}
		appState.formInvalid = false
		if (save) {
			message = await save(form)
		}
	}
</script>

<svelte:window {onkeydown} />

{#if isVisible}
	<div
		onclick={onbackdropclick}
		transition:fade={{ duration: 150 }}
		class="tw:fixed tw:inset-0 tw:bg-black/50 tw:z-1040"
		role="presentation">
	</div>

	<div
		{id}
		class="tw:fixed tw:inset-0 tw:z-[1050] tw:overflow-y-auto tw:flex tw:items-start tw:justify-center"
		transition:fly={{ y: -50, duration: 300 }}
		tabindex="-1"
		aria-labelledby={titleId}
		aria-hidden={!isVisible}
		aria-modal={isVisible}
		role="dialog">
		<div class="tw:relative tw:w-full tw:mx-4 tw:my-6 tw:max-w-100" class:tw:max-w-200={large}>
			<div
				class="tw:relative tw:flex tw:flex-col tw:w-full tw:text-gray-900 tw:pointer-events-auto tw:bg-white tw:border tw:border-gray-300 tw:rounded-md">
				<div class="tw:flex tw:items-center tw:justify-between tw:px-4 tw:pt-4">
					<SectionHeading id={titleId}>{title}</SectionHeading>
					<button
						onclick={close}
						type="button"
						class="tw:p-0 tw:border-0 tw:mb-1 tw:bg-transparent tw:text-plum tw:hover:text-amber-500"
						aria-label="Close">
						<svg height="24" width="24">
							<title>Close modal</title>
							<use href="/images/icons/icons.svg#x" />
						</svg>
					</button>
				</div>

				<form {onsubmit} bind:this={form} autocomplete={autocomplete ? 'on' : undefined} novalidate>
					<div class="tw:relative tw:flex-1 tw:p-4">
						{@render children?.()}
						{#if message}
							<p class="tw:text-red-500">{message}</p>
						{/if}
					</div>
					{#if save}
						<div class="tw:flex tw:justify-end tw:gap-2 tw:p-4 tw:border-t tw:border-gray-200">
							<button
								onclick={close}
								type="button"
								class="tw:w-30 tw:bg-gray-500 tw:text-white tw:py-2 tw:px-4 tw:rounded-md tw:hover:bg-gray-600">
								Cancel
							</button>
							<button
								type="submit"
								class="tw:w-30 tw:bg-plum/90 tw:text-white tw:py-2 tw:px-4 tw:rounded-md tw:hover:bg-plum">
								Save
							</button>
						</div>
					{/if}
				</form>
			</div>
		</div>
	</div>
{/if}
